import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
  Box,
  Autocomplete,
} from '@mui/material';
import {
  GetModelNameList,
  PostRepairRequest,
} from '../../../services/apiTypes';
import { useTranslation } from 'react-i18next';
import { AppDispatch, RootState } from '../../../stores/store';
import { useDispatch, useSelector } from 'react-redux';
import {
  createRepairRequest,
  fetchRepairRequest,
} from '../../../stores/repairRequest/slice';
import {
  DATE_FORMAT,
  INSPEX_PATHNAME,
  VALID_NUMBER_DIGITS,
} from '../../../_constants/Const';
import {
  HISTORY_STATUSES,
  HISTORY_TYPE,
  INSPECTION_STATUSES,
} from '../../../_constants/inspectionConst';
import { isGetRepairRequest } from '../../../_utils/typeGuardUtils';
import useErrorModal from '../../../hooks/useErrorModal';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { isFulfilled, isRejected } from '@reduxjs/toolkit';
import {
  adjustInputYear,
  adjustToMaxDigits,
  isStringInput,
} from '../../../_utils/utils';
import { MaxLength } from '../../../_constants/maxLengthConst';
import { HIDE_SPIN_BUTTON_STYLE } from '../../../_constants/styleConst';
import { API_CODE, CUSTOM_ERROR_CODE } from '../../../_constants/apiConst';
import { CustomError } from '../../../services/CustomError';
import { useNavigate } from 'react-router-dom';
import ErrorModal from '../../../_common/Modal/ErrorModal';
import useSaveHistory from '../../../hooks/useSaveHistory';

const styleButton = {
  width: '120px',
  mx: 2,
  my: 1,
};

type CreateRequestModalProps = {
  isOpen: boolean;
  handleClose: (isClose: boolean) => void;
  modelList: GetModelNameList;
};

const defaultRequestData: PostRepairRequest = {
  rmaNo: '',
  status: INSPECTION_STATUSES.INSPECTION_SERIAL_CHECK,
  serialNo: '',
  model: '',
  customerName: '',
  scopeConnectionCount: null,
  ownership: '',
  installDate: '',
  originalCustomer: '',
  customerComment: '',
  warrantyEndDate: '',
  contract: '',
  distributor: '',
  contractEndDate: '',
  customerPriority: '',
};

const requiredFields: (keyof PostRepairRequest)[] = [
  'rmaNo',
  'serialNo',
  'model',
];

/** 起票のダイアログ */
const RegisterModal: React.FC<CreateRequestModalProps> = ({
  isOpen,
  modelList,
  handleClose,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { error, showApiError, showTypeError, hideError, getApiErrorMessage } =
    useErrorModal();
  const saveHistory = useSaveHistory();
  const [newRequest, setNewRequest] =
    useState<PostRepairRequest>(defaultRequestData);
  // 登録ボタンが非活性か
  const [isDisabledAddBtn, setIsDisabledAddBtn] = useState(true);
  // 登録に失敗したか
  const [errorMsg, setErrorMsg] = useState('');
  const { userData } = useSelector((state: RootState) => state.userInfo);

  // 処理中フラグ
  const isProcessing = useRef(false);

  useEffect(() => {
    const isAllInput = requiredFields.every((field) =>
      isStringInput(newRequest[field]),
    );
    setIsDisabledAddBtn(!isAllInput);
  }, [newRequest]);

  useEffect(() => {
    setNewRequest(defaultRequestData);
    setErrorMsg('');
  }, [isOpen]);

  /** 修理依頼をPOSTしてstoreにセット */
  const setRequest = async () => {
    const response = await dispatch(createRepairRequest(newRequest));
    // 登録に失敗
    if (isRejected(response)) {
      if (response.payload instanceof CustomError) {
        const { code, data } = response.payload;
        const value =
          code === CUSTOM_ERROR_CODE.NO_TEMPLATE // dataがinspectionClass('1')なので"受入診断"に変換
            ? [t('LBL_STATUS_INSPECTION')]
            : data;
        response.payload.data = value;
        setErrorMsg(
          getApiErrorMessage(response.payload, API_CODE.REPAIR_REQUEST),
        );
      } else {
        setErrorMsg(
          getApiErrorMessage(response.payload, API_CODE.REPAIR_REQUEST),
        );
      }
      return;
    }

    // 登録に成功
    handleClose(true);

    // 機種情報を取得するためGETを実行
    const repairRequest = await dispatch(
      fetchRepairRequest({ rmaNo: newRequest.rmaNo }),
    );
    if (isFulfilled(repairRequest)) {
      if (!isGetRepairRequest(repairRequest.payload)) {
        showTypeError(API_CODE.REPAIR_REQUEST);
        return;
      }
    } else {
      showApiError(repairRequest.payload, API_CODE.REPAIR_REQUEST);
      return;
    }

    /** 操作履歴(受入診断のCreate)を保存 */
    saveHistory(
      repairRequest.payload,
      HISTORY_STATUSES.INSPECTION.id,
      HISTORY_TYPE.CREATE.id,
      () => navigate(INSPEX_PATHNAME.SERIAL_CHECK),
      showApiError,
    );
  };

  /** 追加ボタン押下 */
  const onClickAdd = async () => {
    if (isProcessing.current) {
      // 連打防止
      return;
    }
    isProcessing.current = true;
    setErrorMsg('');
    try {
      await setRequest();
    } finally {
      isProcessing.current = false;
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    const fieldName = name as keyof PostRepairRequest;

    if (fieldName === 'scopeConnectionCount') {
      // 正の値のみ入力可、テキスト直入力への対応
      let newValue = value !== '' ? Math.floor(Number(value)) : null;
      if (newValue !== null) {
        newValue = adjustToMaxDigits(newValue, VALID_NUMBER_DIGITS.FOUR);
      }
      setNewRequest({ ...newRequest, [fieldName]: newValue });
      return;
    }

    if (typeof newRequest[fieldName] === 'number') {
      setNewRequest({ ...newRequest, [fieldName]: Number(value) });
    } else {
      setNewRequest({ ...newRequest, [fieldName]: value });
    }
  };

  const handleInputDateChange = (
    value: string | number | Dayjs | null,
    name: keyof PostRepairRequest,
  ) => {
    let date = '';
    if (value instanceof Object) {
      // format通りの年月日の形式にする
      const formattedDate =
        value.year().toString() +
        '-' +
        Number(value.month() + 1).toString() +
        '-' +
        value.date().toString();
      const adjustedDate = adjustInputYear(dayjs(formattedDate));
      const formatString =
        userData.dateFormat === 2 // DD-MM-YY → DD-MM-YYYY
          ? DATE_FORMAT[1]
          : DATE_FORMAT[userData.dateFormat];
      date = adjustedDate ? dayjs(adjustedDate).format(formatString) : '';
    }

    setNewRequest({
      ...newRequest,
      [name]: date,
    });
  };

  return (
    <>
      <Dialog open={isOpen} onClose={handleClose} closeAfterTransition={false}>
        <Box display="flex">
          <DialogTitle>{t('LBL_REGISTER')}</DialogTitle>
          <Typography alignSelf="center" color={theme.palette.error.main}>
            {errorMsg}
          </Typography>
        </Box>
        <DialogContent sx={{ padding: '0.5rem 2rem' }}>
          <TextField
            autoFocus
            margin="dense"
            label={t('LBL_RMA_NO')}
            type="text"
            fullWidth
            name="rmaNo"
            sx={{ bgcolor: 'warning.light' }}
            value={newRequest.rmaNo}
            onChange={handleInputChange}
            slotProps={{
              htmlInput: { maxLength: MaxLength.RegisterModal.RMA_NO },
            }}
          />
          <TextField
            margin="dense"
            label={t('LBL_SERIAL_NO')}
            type="text"
            fullWidth
            name="serialNo"
            sx={{ bgcolor: 'warning.light' }}
            value={newRequest.serialNo}
            onChange={handleInputChange}
            slotProps={{
              htmlInput: { maxLength: MaxLength.RegisterModal.SERIAL_NO },
            }}
          />
          <Autocomplete
            options={modelList}
            noOptionsText={t('MSG_NO_OPTION')}
            disableClearable={newRequest.model === ''}
            value={newRequest.model}
            onChange={(_, newValue) => {
              handleInputChange({
                target: {
                  name: 'model',
                  value: newValue ?? '',
                },
              } as React.ChangeEvent<HTMLInputElement>);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('LBL_MODEL')}
                margin="dense"
                fullWidth
                sx={{ bgcolor: 'warning.light' }}
              />
            )}
          />
          <TextField
            margin="dense"
            label={t('LBL_SCOPE_CONNECTION_COUNT')}
            type="number"
            fullWidth
            name="scopeConnectionCount"
            value={newRequest.scopeConnectionCount ?? ''}
            onChange={handleInputChange}
            slotProps={{ htmlInput: { min: 0 } }} // 正の値のみ入力可
            sx={HIDE_SPIN_BUTTON_STYLE}
          />
          <TextField
            margin="dense"
            label={t('LBL_OWNERSHIP')}
            type="text"
            fullWidth
            name="ownership"
            value={newRequest.ownership}
            onChange={handleInputChange}
            slotProps={{
              htmlInput: { maxLength: MaxLength.RegisterModal.OWNERSHIP },
            }}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label={t('LBL_INSTALL_DATE')}
              value={
                newRequest.installDate === ''
                  ? null
                  : dayjs(newRequest.installDate)
              }
              onChange={(value) => handleInputDateChange(value, 'installDate')}
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: 'dense',
                },
                field: {
                  clearable: true,
                  onClear: () => handleInputDateChange('', 'installDate'),
                },
              }}
              format={DATE_FORMAT[userData.dateFormat]}
              views={['year', 'month', 'day']}
            />
          </LocalizationProvider>
          <TextField
            margin="dense"
            label={t('LBL_CUSTOMER')}
            type="text"
            fullWidth
            name="customerName"
            value={newRequest.customerName}
            onChange={handleInputChange}
            slotProps={{
              htmlInput: { maxLength: MaxLength.RegisterModal.CUSTOMER },
            }}
          />
          <TextField
            margin="dense"
            label={t('LBL_ORIGINAL_CUSTOMER')}
            type="text"
            fullWidth
            name="originalCustomer"
            value={newRequest.originalCustomer}
            onChange={handleInputChange}
            slotProps={{
              htmlInput: {
                maxLength: MaxLength.RegisterModal.ORIGINAL_CUSTOMER,
              },
            }}
          />
          <TextField
            margin="dense"
            label={t('LBL_CUSTOMER_PRIORITY')}
            type="text"
            fullWidth
            name="customerPriority"
            slotProps={{
              htmlInput: {
                maxLength: MaxLength.RegisterModal.CUSTOMER_PRIORITY,
              },
            }}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            label={t('LBL_DISTRIBUTOR')}
            type="text"
            fullWidth
            name="distributor"
            value={newRequest.distributor}
            onChange={handleInputChange}
            slotProps={{
              htmlInput: { maxLength: MaxLength.RegisterModal.DISTRIBUTOR },
            }}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label={t('LBL_WARRANTY_END_DATE')}
              value={
                newRequest.warrantyEndDate === ''
                  ? null
                  : dayjs(newRequest.warrantyEndDate)
              }
              onChange={(value) =>
                handleInputDateChange(value, 'warrantyEndDate')
              }
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: 'dense',
                },
                field: {
                  clearable: true,
                  onClear: () => handleInputDateChange('', 'warrantyEndDate'),
                },
              }}
              format={DATE_FORMAT[userData.dateFormat]}
              views={['year', 'month', 'day']}
            />
          </LocalizationProvider>
          <TextField
            margin="dense"
            label={t('LBL_CONTRACT_NUMBER')}
            type="text"
            fullWidth
            name="contract"
            value={newRequest.contract}
            onChange={handleInputChange}
            slotProps={{
              htmlInput: {
                maxLength: MaxLength.RegisterModal.CONTRACT_NUMBER,
              },
            }}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label={t('LBL_CONTRACT_END_DATE')}
              value={
                newRequest.contractEndDate === ''
                  ? null
                  : dayjs(newRequest.contractEndDate)
              }
              onChange={(value) =>
                handleInputDateChange(value, 'contractEndDate')
              }
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: 'dense',
                },
                field: {
                  clearable: true,
                  onClear: () => handleInputDateChange('', 'contractEndDate'),
                },
              }}
              format={DATE_FORMAT[userData.dateFormat]}
              views={['year', 'month', 'day']}
            />
          </LocalizationProvider>
          <TextField
            margin="dense"
            label={t('LBL_CUSTOMER_COMMENT')}
            type="text"
            fullWidth
            multiline
            name="customerComment"
            value={newRequest.customerComment}
            onChange={handleInputChange}
            slotProps={{
              htmlInput: {
                maxLength: MaxLength.RegisterModal.CUSTOMER_COMMENT,
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            sx={styleButton}
            variant="contained"
            onClick={() => handleClose(true)}
            color="inherit"
          >
            {t('BTN_CANCEL')}
          </Button>
          <Button
            sx={styleButton}
            variant="contained"
            onClick={onClickAdd}
            color="primary"
            disabled={isDisabledAddBtn || isProcessing.current}
          >
            {t('BTN_ADD')}
          </Button>
        </DialogActions>
      </Dialog>
      {error && <ErrorModal message={error} onClose={hideError} />}
    </>
  );
};

export default RegisterModal;
